import {pick} from '@benchling/js-utils';

import {TagSchemaConstraint} from 'constants/RegistryConstants';
import {
  SchemaInterfaceFieldNode,
  TagSchemaConstraintNode,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {FieldType, InterfaceField, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {omit} from 'util/omitKey';
import {MakeFieldsOptional} from 'util/Types';
import uniqueId from 'util/uniqueId';

export function coerceConstraintShape(
  interfaceConstraint: Omit<TagSchemaConstraintNode, 'id' | 'source' | 'typeSpecificConstraintConfig'> | null
): Omit<TagSchemaConstraint, 'id' | 'source' | 'typeSpecificConstraintConfig'> | null {
  return interfaceConstraint
    ? {
        ...omit(interfaceConstraint, '__typename', 'fieldsJson'),
        fields: interfaceConstraint.fieldsJson,
      }
    : null;
}

export type ClientInterfaceField = MakeFieldsOptional<InterfaceField, 'id' | 'apiId'>;

export const convertFromClientInterfaceFieldToSchemaField = (
  interfaceField: ClientInterfaceField
): ClientSchemaField => ({
  ...interfaceField,
  name: interfaceField.displayName,
  dbId: interfaceField.id,
  archiveRecord: null,
  cid: uniqueId(),
  isComputed: interfaceField.isComputed,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
});

type SchemaInterfaceFieldNodeAttributesUsedForClientInterfaceField = Pick<
  SchemaInterfaceFieldNode,
  | 'systemName'
  | 'displayName'
  | 'isRequired'
  | 'isMulti'
  | 'isComputed'
  | 'schemaFieldSelectorId'
  | 'requiredLinkJson'
  | 'fieldType'
> & {
  unit?: {id: string; symbol: string} | null;
};

export const convertGQLSchemaInterfaceFieldToClientInterfaceField = <
  T extends Omit<Partial<SchemaInterfaceFieldNode>, 'unit'> &
    SchemaInterfaceFieldNodeAttributesUsedForClientInterfaceField,
>(
  interfaceField: T
): ClientInterfaceField => {
  const baseField = pick(
    interfaceField,
    'systemName',
    'displayName',
    'isRequired',
    'isMulti',
    'isComputed',
    'schemaFieldSelectorId',
    'requiredLinkJson',
    'fieldType',
    'apiId',
    'id'
  );

  // Convert GraphQL unit format to client format
  const unitApiIdentifier = interfaceField.unit?.id || null;
  const unitSymbol = interfaceField.unit?.symbol || null;

  return convertGQLSchemaInterfaceFieldToPartialClientInterfaceField({
    ...baseField,
    unitApiIdentifier,
    unitSymbol,
  });
};

// We can export this if there is a need for it in the future.
// Primarily it allows us to pass in a subset of attributes and receive a typesafe subset back.
const convertGQLSchemaInterfaceFieldToPartialClientInterfaceField = <
  T extends Partial<SchemaInterfaceFieldNode> & {
    requiredLinkJson: SchemaInterfaceFieldNode['requiredLinkJson'];
    fieldType: SchemaInterfaceFieldNode['fieldType'];
    unitApiIdentifier?: string | null;
    unitSymbol?: string | null;
  },
>(
  interfaceField: T
): Omit<T, '__typename' | 'requiredLinkJson' | 'unit'> & {
  fieldType: FieldType;
  requiredLink: T['requiredLinkJson'];
} => ({
  ...omit(interfaceField, '__typename', 'requiredLinkJson', 'unit'),
  fieldType: interfaceField.fieldType as FieldType,
  requiredLink: interfaceField.requiredLinkJson,
});

// This is a list of attributes on a ClientSchemaField that can be modified and maintained by a fieldset.
// Keep in sync with _FIELDSET_CONTROLLED_FIELD_ATTRIBUTES in benchling/schemas/core/tasks/apply_fieldset_task.py.
export const FIELDSET_CONTROLLED_FIELD_ATTRIBUTES: Array<keyof SchemaField> = [
  'archiveRecord',
  'name',
  'isRequired',
  'isMulti',
  'isComputed',
  'isParentLink',
  'fieldType',
  'systemName',
  'displayName',
  'requiredLink',
  'schemaFieldSelectorId',
  'unitApiIdentifier',
  'unitSymbol',
];
